import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Client } from '../models/clientsModel';
import type { Project } from '../models/projectModel';
import type { User } from '../models/userModel';

// Update thunk to fetch both clients and projects
export const loadAppData = createAsyncThunk<
  { user: User, clients: Client[]; projects: Project[] },
  { makeAuthenticatedApiCall: (url: string, options: any) => Promise<any> }
>(
  'appData/load',
  async ({ makeAuthenticatedApiCall }, { rejectWithValue }) => {
    try {
      // Fetch user details
      const userResponse = await makeAuthenticatedApiCall('/api/Users/Me', {
        method: 'GET',
      });
      if (!userResponse) {
        return rejectWithValue('No response from User API');
      }
      if (userResponse === undefined || userResponse === null) {
        return rejectWithValue('User data is empty');
      }
      console.log('User data fetched:', userResponse);
      const user: User = userResponse;

      // Fetch clients
      const clientsResponse = await makeAuthenticatedApiCall('/api/Client/Get', {
        method: 'POST',
      });
      if (!clientsResponse) {
        return rejectWithValue('No response from Client API');
      }
      const clientsData = Array.isArray(clientsResponse) ? clientsResponse : clientsResponse;

      // Fetch projects
      const projectsResponse = await makeAuthenticatedApiCall('/api/Project/Get', {
        method: 'GET',
      });
      if (!projectsResponse) {
        return rejectWithValue('No response from Project API');
      }
      const projectsData = Array.isArray(projectsResponse) ? projectsResponse : projectsResponse;

      return { user: user, clients: clientsData, projects: projectsData };
    } catch (err) {
      console.error('Error loading app data:', err);
      return rejectWithValue('Failed to load app data');
    }
  }
);

interface AppDataState {
  user: User;
  clients: Client[];
  projects: Project[];
  loading: boolean;
  error: string | null;
}

const initialState: AppDataState = {
  user: {
    Id: '',
    userId: '',
    userName: '',
    email: '',
    firstName: '',
    lastName: '',
    status: '',
  },
  clients: [],
  projects: [],
  loading: false,
  error: null,
};

const appDataSlice = createSlice({
  name: 'appData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAppData.pending, (state) => {
        console.log('Loading app data...');
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAppData.fulfilled, (state, action) => {
        console.log('App data loaded:', action.payload);
        state.clients = action.payload.clients;
        state.projects = action.payload.projects;
        state.loading = false;
      })
      .addCase(loadAppData.rejected, (state, action) => {
        console.error('Failed to load app data:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default appDataSlice.reducer;

